import axiosInstance from '../config/axios';

// User APIs
export const userApi = {
  getCRMUsers: () => axiosInstance.get('/api/auth/users/crm'),
};

// Company APIs
export const companyApi = {
  getAll: (params?: any) => axiosInstance.get('/api/companies', { params }),
  getById: (id: string) => axiosInstance.get(`/api/companies/${id}`),
  create: (data: any) => axiosInstance.post('/api/companies', data),
  update: (id: string, data: any) => axiosInstance.put(`/api/companies/${id}`, data),
  delete: (id: string) => axiosInstance.delete(`/api/companies/${id}`),
  getStats: (id: string) => axiosInstance.get(`/api/companies/${id}/stats`),
  addContact: (companyId: string, contactId: string) => 
    axiosInstance.post(`/api/companies/${companyId}/contacts/${contactId}`),
  removeContact: (companyId: string, contactId: string) => 
    axiosInstance.delete(`/api/companies/${companyId}/contacts/${contactId}`),
};

// Contact APIs
export const contactApi = {
  getAll: (params?: any) => axiosInstance.get('/api/contacts', { params }),
  getById: (id: string) => axiosInstance.get(`/api/contacts/${id}`),
  create: (data: any) => axiosInstance.post('/api/contacts', data),
  update: (id: string, data: any) => axiosInstance.put(`/api/contacts/${id}`, data),
  delete: (id: string) => axiosInstance.delete(`/api/contacts/${id}`),
  getStats: (id: string) => axiosInstance.get(`/api/contacts/${id}/stats`),
  getInteractions: (id: string, params?: any) => 
    axiosInstance.get(`/api/contacts/${id}/interactions`, { params }),
  searchAutocomplete: (query: string) => 
    axiosInstance.get('/api/contacts/search/autocomplete', { params: { q: query } }),
};

// Deal APIs
export const dealApi = {
  getAll: (params?: any) => axiosInstance.get('/api/deals', { params }),
  getById: (id: string) => axiosInstance.get(`/api/deals/${id}`),
  create: (data: any) => axiosInstance.post('/api/deals', data),
  update: (id: string, data: any) => axiosInstance.put(`/api/deals/${id}`, data),
  delete: (id: string) => axiosInstance.delete(`/api/deals/${id}`),
  getStats: (id: string) => axiosInstance.get(`/api/deals/${id}/stats`),
  getInteractions: (id: string, params?: any) => 
    axiosInstance.get(`/api/deals/${id}/interactions`, { params }),
  getPipelineStats: (params?: any) => 
    axiosInstance.get('/api/deals/pipeline/stats', { params }),
  getUpcomingFollowUps: () => 
    axiosInstance.get('/api/deals/followups/upcoming'),
};

// CRM Settings APIs
export const crmSettingsApi = {
  getByType: (type: string, includeInactive?: boolean) => 
    axiosInstance.get(`/api/crm-settings/${type}`, { 
      params: { includeInactive } 
    }),
  getAll: (includeInactive?: boolean) => 
    axiosInstance.get('/api/crm-settings', { 
      params: { includeInactive } 
    }),
  create: (data: any) => axiosInstance.post('/api/crm-settings', data),
  update: (id: string, data: any) => 
    axiosInstance.put(`/api/crm-settings/${id}`, data),
  delete: (id: string) => axiosInstance.delete(`/api/crm-settings/${id}`),
  initializeDefaults: () => 
    axiosInstance.post('/api/crm-settings/initialize/defaults'),
  reorder: (type: string, orderedIds: string[]) => 
    axiosInstance.put(`/api/crm-settings/reorder/${type}`, { orderedIds }),
};

// Interaction APIs
export const interactionApi = {
  getAll: (params?: any) => axiosInstance.get('/api/interactions', { params }),
  getById: (id: string) => axiosInstance.get(`/api/interactions/${id}`),
  create: (data: any) => axiosInstance.post('/api/interactions', data),
  update: (id: string, data: any) => 
    axiosInstance.put(`/api/interactions/${id}`, data),
  delete: (id: string) => axiosInstance.delete(`/api/interactions/${id}`),
  completeFollowUp: (id: string) => 
    axiosInstance.put(`/api/interactions/${id}/complete-followup`),
  getPendingFollowUps: (date?: string) => 
    axiosInstance.get('/api/interactions/followups/pending', { 
      params: { date } 
    }),
  getOverdueFollowUps: () => 
    axiosInstance.get('/api/interactions/followups/overdue'),
  getUpcomingFollowUps: (days?: number) => 
    axiosInstance.get('/api/interactions/followups/upcoming', { 
      params: { days } 
    }),
  getStats: (startDate?: string, endDate?: string) =>
    axiosInstance.get('/api/interactions/stats/overview', {
      params: { startDate, endDate }
    }),
  addComment: (id: string, data: any) =>
    axiosInstance.post(`/api/interactions/${id}/comments`, data),
  updateComment: (id: string, commentId: string, data: any) =>
    axiosInstance.put(`/api/interactions/${id}/comments/${commentId}`, data),
  deleteComment: (id: string, commentId: string) =>
    axiosInstance.delete(`/api/interactions/${id}/comments/${commentId}`),
};

// Helper function to format API errors
export const formatApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};